import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, ShieldCheck, KeyRound } from 'lucide-react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { adminLogin, adminVerifyCode } from '../services/adminApi';

const LOGOUT_REASON_MESSAGES = {
  forbidden: 'Bu hesabın admin paneline erişim yetkisi yok.',
  unauthorized: 'Oturumunuzun süresi doldu, tekrar giriş yapın.',
};

const AdminLoginPage = () => {
  const { isAuthenticated, login, logoutReason, clearLogoutReason } = useAdminAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tokenId, setTokenId] = useState(null);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/panel" replace />;
  }

  const requestLogin = async () => {
    const res = await adminLogin(email, password);

    if (!res.ok || res.status === false) {
      setError(res.message || 'E-posta veya şifre hatalı.');
      return false;
    }

    if (res.data?.isTwoFactor) {
      setTokenId(res.data.tokenId);
      setStep('otp');
      setCode('');
      setInfo('Doğrulama kodu telefonunuza SMS ile gönderildi.');
      return true;
    }

    if (res.data?.token) {
      login(res.data);
      navigate('/panel', { replace: true });
      return true;
    }

    setError('Beklenmeyen bir yanıt alındı.');
    return false;
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    clearLogoutReason();
    setLoading(true);
    try {
      await requestLogin();
    } catch {
      setError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await adminVerifyCode(tokenId, code);

      if (res.httpStatus === 404) {
        setError('Kod hatalı veya süresi dolmuş.');
        setCode('');
        return;
      }

      if (!res.ok || res.status === false || !res.data?.token) {
        setError(res.message || 'Kod doğrulanamadı.');
        return;
      }

      login(res.data);
      navigate('/panel', { replace: true });
    } catch {
      setError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const ok = await requestLogin();
      if (ok) setInfo('Yeni kod gönderildi.');
    } catch {
      setError('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const bannerMessage = error || (logoutReason && !info ? LOGOUT_REASON_MESSAGES[logoutReason] : '');

  return (
    <div className="admin-login-screen">
      <div className="admin-login-card">
        <div className="admin-login-logo">
          <ShieldCheck size={22} color="#2F3A8F" />
          <span>isBulsun</span>
          <span className="admin-login-badge">Admin</span>
        </div>
        <p className="admin-login-sub">
          {step === 'credentials' ? 'Yönetim paneline giriş yap' : 'Telefonuna gelen kodu gir'}
        </p>

        {bannerMessage && <div className="admin-error">{bannerMessage}</div>}
        {info && !error && <div className="admin-info">{info}</div>}

        {step === 'credentials' ? (
          <form onSubmit={handleCredentialsSubmit}>
            <div className="admin-field">
              <label htmlFor="admin-email">E-posta</label>
              <div className="admin-input-wrap">
                <Mail />
                <input
                  id="admin-email"
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
            </div>

            <div className="admin-field">
              <label htmlFor="admin-password">Şifre</label>
              <div className="admin-input-wrap">
                <Lock />
                <input
                  id="admin-password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
            </div>

            <button type="submit" className="admin-submit" disabled={loading}>
              {loading ? <Loader2 size={16} className="spin" /> : null}
              {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleCodeSubmit}>
            <div className="admin-field">
              <label htmlFor="admin-code">Doğrulama Kodu</label>
              <div className="admin-input-wrap">
                <KeyRound />
                <input
                  id="admin-code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  className="admin-otp-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                />
              </div>
            </div>

            <button type="submit" className="admin-submit" disabled={loading || code.length !== 6}>
              {loading ? <Loader2 size={16} className="spin" /> : null}
              {loading ? 'Doğrulanıyor...' : 'Doğrula'}
            </button>

            <button type="button" className="admin-link-btn" onClick={handleResend} disabled={loading}>
              Kodu tekrar gönder
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default AdminLoginPage;
